/**
 * Shopify App Store review prompt preferences.
 * 冷却以服务端 ad_user.review_prompt_* 为准（换浏览器仍生效）；
 * localStorage 只作同机即时缓存。Neutral solicit only — no incentives.
 */
import { trackAppReviewEvent } from '@/api/dashboard'
import { isAdminEnter } from '@/utils/admin-enter'

/**
 * 评价页 + 官方弹层 hash。
 * 已登录 Shopify：WriteReviewModal 直接开，少一步。
 * 没登录：跳转可能丢掉 hash，人仍停在 /reviews，主按钮就是 Write a review。
 */
export const REVIEW_URL =
  'https://apps.shopify.com/honest-fulphilment/reviews#modal-show=WriteReviewModal'

const DAY = 24 * 60 * 60 * 1000
/** Maybe later 之后多久再问（原先 30 天，等于放走一个月；2026-10-21 改成 7 天） */
const LATER_MS = 7 * DAY
/** After opening the App Store link, wait before asking again */
const AFTER_OPEN_MS = 180 * DAY

function key(userId) {
  return `hf_app_review_${userId}`
}

function read(userId) {
  if (!userId) return null
  try {
    const raw = localStorage.getItem(key(userId))
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

function write(userId, data) {
  if (!userId) return
  try {
    localStorage.setItem(key(userId), JSON.stringify(data))
  } catch (e) {
    // private mode / quota
  }
}

export function markReviewLater(userId) {
  write(userId, { laterUntil: Date.now() + LATER_MS })
}

export function markReviewNever(userId) {
  write(userId, { never: true })
}

export function markReviewOpened(userId) {
  write(userId, { laterUntil: Date.now() + AFTER_OPEN_MS })
}

/**
 * @param {{ userId: string|number, hasShopify: boolean, hasDelivered: boolean, adminEnter: boolean, blocked: boolean, force?: boolean, reviewPromptNever?: boolean, reviewPromptUntil?: string|number|Date }} opts
 * hasDelivered = 账号有过已签收的订单（不限所选时间/店铺）
 * reviewPromptNever / reviewPromptUntil 来自服务端（换浏览器仍生效）；localStorage 只作同机即时缓存
 */
export function shouldShowReviewPrompt(opts) {
  const {
    userId, hasShopify, hasDelivered, adminEnter, blocked, force,
    reviewPromptNever, reviewPromptUntil,
  } = opts || {}
  if (adminEnter || blocked) return false
  // ?review_prompt=1 预览：跳过签收/店铺/冷却条件
  if (force) return true
  if (!userId || !hasShopify || !hasDelivered) return false
  if (reviewPromptNever) return false
  if (reviewPromptUntil && Date.now() < new Date(reviewPromptUntil).getTime()) return false
  const state = read(userId)
  if (!state) return true
  if (state.never) return false
  if (state.laterUntil && Date.now() < Number(state.laterUntil)) return false
  return true
}

/**
 * 常驻入口（顶部导航星标）打开评价页：先记一次 leave_review 点击 + 写冷却，再新窗打开。
 * 管理端代登不写 GA，避免把运营的点击算进商家数据。
 * @param {string|number} userId
 * @param {string} source dashboard_prompt | navbar
 */
export function openReviewPage(userId, source) {
  if (!isAdminEnter()) {
    markReviewOpened(userId)
    trackAppReviewEvent('click', 'leave_review', { source }).catch(() => {})
  }
  window.open(REVIEW_URL, '_blank', 'noopener,noreferrer')
}
